// "AdvancedMD reports" — Setup > Data card (2026-10-21).
//
// A practice billing from AdvancedMD (pm_system "advancedmd") feeds Atlas by hand: each
// AdvancedMD report, saved as the Report Center PDF (or exported with the report's own
// Excel button), is uploaded here in a fixed order (patients, then fees, then claims,
// then the Period Reports: the transaction ledger, the deposits, the Management Summary
// that feeds the tie-out, then the rest). The server converts, matches and writes (services/advancedmd_report_service.py
// and, for the Waystar EOB, routes/advancedmd_eob.py); this posts a file per row and
// renders the counts. Nothing here names a patient: the response is counts and
// warnings.
//
//   GET  /api/practices/<id>/advancedmd-report          applies, running, kinds[]
//   POST /api/practices/<id>/advancedmd-report/<kind>   multipart: file (eob: many), dry_run
//
// Every upload is synchronous: the POST answers 200 with its counts (a dry run counts
// and changes nothing), 400 with a reason, 409 while another import holds the practice.

import React, { useCallback, useEffect, useState } from "react";

import { formatWhen } from "./DataPage";
import "./AdvancedmdReportCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const HINT =
  "Run each report in AdvancedMD for 1/1/2026–12/31/2026 (the Period Reports as Open Transactions) " +
  "and save it as the Report Center PDF (or the report's own Excel export where there is one; never " +
  "Save Page); upload in this order: Demographics → Fee Schedule → Carrier A/R Detail → Analysis of " +
  "Services Detail → Deposit Totals Detail → Management Summary (tie-out) → Appointments → Denials → EOBs.";

// The upload order, and what each row is called when the server's status carries no
// `kinds` (an older server, or a field left out). The server's own metadata wins.
export const DEFAULT_KINDS = [
  { key: "demographics", label: "Demographic Listing" },
  { key: "fee_schedule", label: "Fee Schedule Listing" },
  { key: "carrier_ar", label: "Carrier A/R Detail" },
  { key: "transactions", label: "Analysis of Services - Detail" },
  { key: "deposits", label: "Deposit Totals - Detail" },
  { key: "management_summary", label: "Management Summary (tie-out)", accept: ".pdf,.txt" },
  { key: "appointments", label: "Master Appointments with Insurance" },
  { key: "denials", label: "Carrier Denial Analysis" },
  { key: "eob", label: "Waystar EOB report", accept: ".txt", multiple: true },
];
const DEFAULT_ACCEPT = ".xlsx,.csv,.xls";

export function advancedmdReportUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/advancedmd-report`;
}

export function kindUrl(practiceId, key) {
  return `${advancedmdReportUrl(practiceId)}/${encodeURIComponent(key)}`;
}

// The rows to render: the server's `kinds` in the server's order when it sends any,
// else the default list. Each row is made tolerant of a missing field.
export function rowsFor(data) {
  const kinds =
    data && Array.isArray(data.kinds) && data.kinds.length > 0
      ? data.kinds
      : DEFAULT_KINDS;
  return kinds
    .filter((k) => k && typeof k.key === "string" && k.key)
    .map((k) => {
      const fallback = DEFAULT_KINDS.find((d) => d.key === k.key) || {};
      return {
        key: k.key,
        label: k.label || fallback.label || k.key,
        report: k.report || "",
        menu: k.menu || "",
        accept: k.accept || fallback.accept || DEFAULT_ACCEPT,
        multiple: k.multiple === true || fallback.multiple === true,
        formats: Array.isArray(k.formats) ? k.formats : (typeof k.formats === 'string' && k.formats ? [k.formats] : []),
        lastRun: k.last_run || null,
      };
    });
}

// "claims: 12, created: 3, updated: 7, total: 1234.50": every count as the server sent
// it, integers and money strings alike. Nothing is named here: the keys are the
// service's own count keys.
export function countsLine(counts) {
  if (!counts || typeof counts !== "object") return "";
  return Object.keys(counts)
    .filter((k) => counts[k] !== null && counts[k] !== undefined)
    .map((k) => `${k.replace(/_/g, " ")}: ${counts[k]}`)
    .join(", ");
}

// "10/13/2026, 6:00:00 PM · ok · 12 rows · by a@b.c", each part only when the run
// carries it; a failed run names its (generic) error.
export function lastRunLine(run) {
  if (!run) return "";
  const parts = [formatWhen(run.uploaded_at)];
  if (run.status) {
    parts.push(
      run.status === "failed" && run.error
        ? `failed (${run.error})`
        : String(run.status),
    );
  }
  if (run.rows !== null && run.rows !== undefined && run.rows !== "") {
    const n = Number(run.rows);
    parts.push(`${n} ${n === 1 ? "row" : "rows"}`);
  }
  if (run.uploaded_by) parts.push(`by ${run.uploaded_by}`);
  return parts.join(" · ");
}

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error((body && body.error) || fallback);
    err.status = response.status;
    throw err;
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

function Message({ kind, text, testId, onClose }) {
  return (
    <div className={`data-message data-message-${kind}`} data-testid={testId}>
      <span>{text}</span>
      <button
        type="button"
        className="data-message-close"
        aria-label="Dismiss"
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
}

const EMPTY_ROW = { files: [], dryRun: false, result: null, error: null, message: null };

export default function AdvancedmdReportCard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // The route is admins-only while the Data page is not, and the card is for a
  // practice billing from AdvancedMD only: for anyone else, or any other practice, the
  // card is not there rather than a red line on every visit.
  const [hidden, setHidden] = useState(false);
  // Per row (by kind key): the chosen files, the dry-run switch, the last response and
  // its messages. One upload at a time across the card: the server holds the
  // practice's import guard while it writes.
  const [rows, setRows] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  const rowState = (key) => rows[key] || EMPTY_ROW;
  const patchRow = (key, patch) =>
    setRows((prev) => ({ ...prev, [key]: { ...(prev[key] || EMPTY_ROW), ...patch } }));

  const load = useCallback(() => {
    if (!practiceId) return Promise.resolve(null);
    return request(
      advancedmdReportUrl(practiceId),
      {},
      "Could not load the AdvancedMD report status.",
    );
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setHidden(false);
    setRows({});
    setBusyKey(null);
    load()
      .then((body) => {
        if (cancelled || !body) return;
        if (body.applies !== true) {
          setHidden(true);
          return;
        }
        setData(body);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) setHidden(true);
        else setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, load]);

  const upload = async (row) => {
    const state = rowState(row.key);
    if (state.files.length === 0 || busyKey) return;
    setBusyKey(row.key);
    patchRow(row.key, { error: null, message: null, result: null });
    const body = new FormData();
    state.files.forEach((f) => body.append("file", f));
    body.append("dry_run", state.dryRun ? "1" : "0");
    try {
      const payload = await request(
        kindUrl(practiceId, row.key),
        { method: "POST", body },
        "The report could not be imported.",
      );
      const line = countsLine(payload.counts);
      patchRow(row.key, {
        files: [],
        result: payload,
        message: payload.dry_run
          ? `Preview: ${line || "no counts"}. Nothing was changed.`
          : `Imported: ${line || "no counts"}.`,
      });
      if (!payload.dry_run) {
        load()
          .then((fresh) => {
            if (fresh && fresh.applies === true) setData(fresh);
          })
          .catch(() => {
            /* the counts are on screen; the stamp refreshes on the next visit */
          });
      }
    } catch (err) {
      patchRow(row.key, { error: err.message });
    } finally {
      setBusyKey(null);
    }
  };

  if (!practiceId || hidden) return null;
  if (!data && !loadError) return null;

  const locked = busyKey !== null;

  return (
    <div className="data-card advancedmd-report" data-testid="advancedmd-report">
      <h2>AdvancedMD reports</h2>
      <p className="data-note">{HINT}</p>

      {loadError && <p className="data-error-line">{loadError}</p>}

      {data &&
        rowsFor(data).map((row) => {
          const state = rowState(row.key);
          const result = state.result;
          const warnings =
            (result && Array.isArray(result.warnings) && result.warnings) ||
            (row.lastRun && Array.isArray(row.lastRun.warnings) && row.lastRun.warnings) ||
            [];
          const checks = (result && Array.isArray(result.checks) && result.checks) || [];
          const busy = busyKey === row.key;
          return (
            <div
              className="amd-row"
              key={row.key}
              data-testid={`advancedmd-row-${row.key}`}
            >
              <div className="amd-row-head">
                <span className="amd-label">{row.label}</span>
                {(row.report || row.menu) && (
                  <span className="amd-source">
                    {row.report}
                    {row.report && row.menu ? " — " : ""}
                    {row.menu}
                  </span>
                )}
              </div>

              <p className="amd-last" data-testid={`advancedmd-last-${row.key}`}>
                {row.lastRun ? (
                  <>
                    Last upload{" "}
                    <span className={row.lastRun.status === "failed" ? "amd-failed" : ""}>
                      {lastRunLine(row.lastRun)}
                    </span>
                    {row.lastRun.status !== "failed" &&
                      row.lastRun.counts &&
                      countsLine(row.lastRun.counts) && (
                        <> &middot; {countsLine(row.lastRun.counts)}</>
                      )}
                  </>
                ) : (
                  "No upload yet."
                )}
              </p>

              {warnings.length > 0 && (
                <ul className="amd-warnings" data-testid={`advancedmd-warnings-${row.key}`}>
                  {warnings.map((w, i) => (
                    <li key={i}>{String(w)}</li>
                  ))}
                </ul>
              )}

              {checks.length > 0 && (
                <ul className="amd-checks" data-testid={`advancedmd-checks-${row.key}`}>
                  {checks.map((c, i) => (
                    <li key={i}>
                      {[
                        c.external_id,
                        c.check_date,
                        c.check_amount !== undefined && c.check_amount !== null
                          ? `$${c.check_amount}`
                          : null,
                        c.created === false ? "already on file" : null,
                        c.claims_matched !== undefined
                          ? `${c.claims_matched} matched`
                          : null,
                        c.claims_unmatched !== undefined
                          ? `${c.claims_unmatched} unmatched`
                          : null,
                      ]
                        .filter((p) => p !== null && p !== undefined && p !== "")
                        .join(" · ")}
                    </li>
                  ))}
                </ul>
              )}

              <div className="amd-upload">
                <label className="amd-file">
                  <span>
                    {row.formats[0] ||
                      (row.multiple ? "One or more files" : "One file")}
                  </span>
                  <input
                    type="file"
                    accept={row.accept}
                    multiple={row.multiple}
                    data-testid={`advancedmd-file-${row.key}`}
                    disabled={locked}
                    onChange={(e) => {
                      const chosen = Array.from(e.target.files || []);
                      if (chosen.length > 0) {
                        patchRow(row.key, {
                          files: row.multiple ? chosen : chosen.slice(0, 1),
                        });
                      }
                      e.target.value = "";
                    }}
                  />
                  {state.files.length > 0 && (
                    <span className="data-filename">
                      {state.files.map((f) => f.name).join(", ")}
                    </span>
                  )}
                </label>
                <label className="amd-switch">
                  <input
                    type="checkbox"
                    data-testid={`advancedmd-dry-run-${row.key}`}
                    checked={state.dryRun}
                    disabled={locked}
                    onChange={(e) => patchRow(row.key, { dryRun: e.target.checked })}
                  />
                  Dry run (count, change nothing)
                </label>
                <button
                  type="button"
                  className="data-button"
                  data-testid={`advancedmd-upload-${row.key}`}
                  disabled={locked || state.files.length === 0}
                  onClick={() => upload(row)}
                >
                  {busy ? "Uploading…" : state.dryRun ? "Dry run" : "Upload"}
                </button>
              </div>

              {state.error && (
                <Message
                  kind="error"
                  text={state.error}
                  testId={`advancedmd-error-${row.key}`}
                  onClose={() => patchRow(row.key, { error: null })}
                />
              )}
              {state.message && (
                <Message
                  kind="success"
                  text={state.message}
                  testId={`advancedmd-message-${row.key}`}
                  onClose={() => patchRow(row.key, { message: null })}
                />
              )}
            </div>
          );
        })}
    </div>
  );
}
